import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TenantsService } from '../../../../services/tenants.service';
import { OrganizationsService } from '../../../../services/organizations.service';
import { Tenant } from '../../../../model/tenant';
import { Organization } from '../../../../model/organization';
import { DeviceCategory } from '../../../../model/device-category';
import { Device } from '../../../../model/device';
import { JsonEditorService } from '../../../../services/json-editor.service';
import { translate } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { JsonEditorComponent } from 'ang-jsoneditor';
import { AuthGuard } from '../../../../common/auth-guard.service';
import { LoggedUser } from '../../../../common/logged-user.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { AppConfig } from '../../../../shared/global-variables';
import { noSpecialCharacters, noWhitespaces } from '../../../../shared/custom-validators';
import { PreviousRouteService } from '../../../../services/previous-route.service';
import {Template} from '../../../../model/template';
import {TemplatesService} from '../../../../services/templates.service';
import {TemplateFilters} from '../../../../model/filters/template-filters';
import {DefaultDeviceCatalog} from '../../../../model/default-device-catalog';
import {DefaultDeviceCatalogFilters} from '../../../../model/filters/default-device-catalog-filters';
import {HwCatalogService} from '../../../../services/hw-catalog.service';
import {DefaultDeviceCategory} from '../../../../model/default-device-category';

@Component({
  selector: 'app-devices-form-info',
  templateUrl: './devices-form-info.component.html',
  styleUrls: ['./devices-form-info.component.scss']
})
export class DevicesFormInfoComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() device: Device;
  @Input() template: Template;
  @Input() defaultDevice: DefaultDeviceCatalog;
  @Output() deviceChange = new EventEmitter<Device>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Output() tenantChange = new EventEmitter<void>();
  @Output() organizationChange = new EventEmitter<void>();
  @Output() templateChange = new EventEmitter<Template>();
  @Output() defaultDeviceChange = new EventEmitter<DefaultDeviceCatalog>();

  deviceForm: FormGroup;
  tenantSelected: boolean;
  organizationSelected: boolean;
  templateDisabled: boolean = false;
  defaultDeviceDisabled: boolean = false;
  previousTemplateUuid: string;
  previousDefaultDeviceUuid: string;

  timezones: any[];
  tenants: Tenant[];
  organizations: Organization[];
  deviceCategories: (DeviceCategory | DefaultDeviceCategory)[];
  tenantDeviceCategories: (DeviceCategory | DefaultDeviceCategory)[];
  templates: Template[];
  defaultDevices: DefaultDeviceCatalog[];
  currentTemplateFilters: TemplateFilters;
  currentDefaultDeviceFilters: DefaultDeviceCatalogFilters;

  technicalDetailsVisible = false;

  @ViewChild('technicalDetailsEditor', {static: false})
  technicalDetailsEditor: JsonEditorComponent;

  constructor(private formBuilder: FormBuilder,
              private tenantsService: TenantsService,
              public jsonEditorService: JsonEditorService,
              private organizationsService: OrganizationsService,
              private templatesService: TemplatesService,
              private hwCatalogService: HwCatalogService,
              private authGuard: AuthGuard,
              private loggedUser: LoggedUser,
              private timezoneService: TimezoneService,
              private messageService: MessageService,
              private previousService: PreviousRouteService) {
    this.currentTemplateFilters = new TemplateFilters();
    this.currentDefaultDeviceFilters = new DefaultDeviceCatalogFilters();
  }

  ngOnInit(): void {
    this.timezones = this.timezoneService.getAllTimezones();
    this.previousTemplateUuid = this.template ? this.template.uuid : null;
    this.previousDefaultDeviceUuid = this.defaultDevice ? this.defaultDevice.uuid : null;
    this.currentTemplateFilters.types = ['DEVICE'];
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    const promises = [this.tenantsService.getTenantSummaries().toPromise()];
    if (this.authGuard.superAdminRequired()) {
      promises.push(this.hwCatalogService.getActiveDefaultDevicesCatalog().toPromise());
    }
    Promise.all(promises).then(response => {
      this.tenants = response[0].tenants;
      if (!this.authGuard.superAdminRequired()) {
        this.device.tenant = this.tenants[0];
      }
      if (this.authGuard.superAdminRequired()) {
        this.defaultDevices = response[1].defaultDevicesCatalog
          .sort((a, b) => a.name > b.name ? 1 : -1);
      }
      this.createForm(this.device);
    });
  }

  private createForm(device: Device): void {
    const tenant = device.tenant ? { name: device.tenant.name, uuid: device.tenant.uuid } : null;
    const organization = device.organization ? { name: device.organization.name, uuid: device.organization.uuid } : null;

    this.deviceForm = this.formBuilder.group({
      serialNumber: [ device.serialNumber, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [ device.description, [Validators.maxLength(255)] ],
      alias: [ device.alias, [Validators.maxLength(255)] ],
      timezone: [ device.timezone ],
      technicalDetails: [ device.technicalDetails ],
      deviceCategory: [ {value: device.deviceCategory, disabled: true}, [Validators.required] ],
      tenant: [ tenant, [Validators.required] ],
      organization: [ {value: organization, disabled: true}, [Validators.required] ],
      uuid: [ device.uuid ]
    });

    this.deviceForm.markAsPending();

    if (this.mode === 'edit') {
      this.deviceForm.get('tenant').disable();
    }

    if (this.device.tenant) {
      this.loadData(this.device.tenant.uuid).then(() => {
        if (!this.authGuard.superAdminRequired()) {
          this.deviceForm.get('tenant').disable();
          this.deviceForm.get('deviceCategory').enable();
          this.templateDisabled = !this.device.deviceCategory;
          this.tenantSelected = true;
          this.loggedUser.userObservable.subscribe(user => {
            if (user.organization) {
              this.tenantsService.getTenantOrganizationSummaries(user.tenant).subscribe(response => {
                this.organizations = response.organizations;
                this.deviceForm.get('organization').setValue(this.organizations[0]);
                this.organizationSelected = true;
              });
            } else {
              this.deviceForm.get('organization').enable();
            }

            if ('ROLE_OPERATOR' === this.loggedUser.user.role.toUpperCase()) {
              this.deviceForm.get('serialNumber').disable();
              this.deviceForm.get('description').disable();
              this.deviceForm.get('alias').disable();
              this.deviceForm.get('tenant').disable();
              this.deviceForm.get('organization').disable();
              this.deviceForm.get('deviceCategory').disable();
              this.deviceForm.get('timezone').disable();
              this.templateDisabled = true;
            }
          });
        } else {
          this.enableTenantDependentElements();
        }
      });
      if (this.device.organization) {
        this.organizationSelected = true;
      }
      if (this.template) {
        this.defaultDeviceDisabled = true;
      }
      if (this.defaultDevice) {
        this.templateDisabled = true;
      }
    } else {
      this.templateDisabled = true;
      this.defaultDeviceDisabled = false;
    }
  }

  private loadData(tenantUuid: string): Promise<any> {
    this.disableTenantDependentElements();
    let promises: Array<Promise<any>> = [this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
      this.tenantsService.getTenantDeviceCategories(tenantUuid).toPromise()];
    if (this.deviceForm.get('deviceCategory').value) {
      this.currentTemplateFilters.deviceCategoryNames = [this.deviceForm.get('deviceCategory').value.name];
      this.currentTemplateFilters.tenantUuids = [tenantUuid];
      promises.push(this.templatesService.getTemplateSummaries(this.currentTemplateFilters).toPromise());
    }
    return Promise.all(promises).then(response => {
      this.organizations = response[0].organizations;
      this.tenantDeviceCategories = response[1].deviceCategories
        .sort((a, b) => a.name > b.name ? 1 : -1);
      if (this.defaultDevice) {
        this.deviceCategories = [this.defaultDevice.defaultDeviceCategory];
        this.deviceForm.get('deviceCategory').enable();
      } else {
        this.deviceCategories = [...this.tenantDeviceCategories];
      }
      if (this.deviceForm.get('deviceCategory').value) {
        this.templates = response[2].templates
          .sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  onChangeTemplate(event: any): void {
    this.defaultDeviceDisabled = !!event.value;
  }

  onChangeDefaultDevice(event: any): void {
    if (event.value) {
      this.templateDisabled = true;
      this.templates = null;
      this.deviceCategories = [event.value.defaultDeviceCategory];
      this.deviceForm.get('deviceCategory').setValue(event.value.defaultDeviceCategory);
    } else {
      if (!this.deviceForm.get('deviceCategory').value) {
        this.templateDisabled = false;
      }
      this.deviceForm.get('deviceCategory').setValue(null);
      this.deviceCategories = this.tenantDeviceCategories ? [...this.tenantDeviceCategories] : [];
    }
  }

  onTenantChange(event: any): void {
    this.tenantChange.emit();
    if (event.value) {
      this.tenantSelected = true;
      this.organizationSelected = false;
      this.deviceForm.get('organization').setValue(null);

      if (this.template) {
        this.deviceForm.get('deviceCategory').setValue(null);
        this.template = null;
      }

      this.currentTemplateFilters.tenantUuids = [event.value.uuid];
      this.loadData(event.value.uuid).then(() => {
        this.enableTenantDependentElements();
      });
    }
  }

  onOrganizationChange(event): void {
    this.organizationChange.emit();
    if (event.value) {
      this.organizationSelected = true;
    }
  }

  onDeviceCategoryChange(event): void {
    if (event.value) {
      this.template = null;
      this.defaultDeviceDisabled = false;
      if (this.tenantSelected) {
        this.currentTemplateFilters.deviceCategoryNames = [event.value.name];
        this.templatesService.getTemplateSummaries(this.currentTemplateFilters).subscribe((response) => {
          this.templates = response.templates;
          this.templateDisabled = false;
        });
      }
    }
  }

  onNextStep(): void {
    if (!this.isTechnicalDetailsValid()) {
      const summary = translate('error.summary.configErrors');
      const detail = translate('error.jsonEditor.configErrors');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
    } else if (this.deviceForm.valid) {
      if ((!this.template && this.previousTemplateUuid) || (this.template && this.template.uuid != this.previousTemplateUuid)) {
        this.templateChange.emit(this.template);
      }
      if ((!this.defaultDevice && this.previousDefaultDeviceUuid) || (this.defaultDevice && this.defaultDevice.uuid != this.previousDefaultDeviceUuid)) {
        this.defaultDeviceChange.emit(this.defaultDevice);
      }
      this.deviceChange.emit(this.deviceForm.getRawValue());
      this.nextStep.emit();
    }
  }

  private isTechnicalDetailsValid(): boolean {
    return this.technicalDetailsEditor.isValidJson();
  }

  private enableTenantDependentElements(): void {
    this.tenantSelected = true;
    this.deviceForm.get('organization').enable();
    if (this.defaultDevice) {
      this.templateDisabled = true;
    } else {
      if (this.deviceForm.get('deviceCategory').value) {
        this.templateDisabled = false;
      }
      this.deviceForm.get('deviceCategory').enable();
    }
  }

  private disableTenantDependentElements(): void {
    this.tenantSelected = false;
    this.templateDisabled = true;
    this.deviceForm.get('organization').disable();
    if (!this.defaultDevice) {
      this.deviceForm.get('deviceCategory').disable();
    }
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

}
